import { useCallback, useMemo, useRef, type ReactNode } from 'react';

import { MapActionsContext, type MapActions } from './MapActionsContext';

/**
 * Bridges imperative map actions from MapView (inside the route <Outlet>) to UI that
 * renders outside it — the Global Stats bar's Reset control lives in the route layout
 * (and is portalled elsewhere on mobile) but must fly the map MapView owns. Scoped to
 * the Find & Fix layout, like ExclusivePanelProvider and StatsScopeProvider.
 *
 * Registration is ref-based (not state) so registering on mount causes no re-render;
 * consumers get stable dispatch callbacks that always reach the latest registration.
 */
export function MapActionsProvider({ children }: { children: ReactNode }) {
    const actionsRef = useRef<MapActions | null>(null);

    const register = useCallback((actions: MapActions | null) => {
        actionsRef.current = actions;
    }, []);

    const flyToNetwork = useCallback(() => {
        actionsRef.current?.flyToNetwork();
    }, []);

    const value = useMemo(() => ({ register, flyToNetwork }), [register, flyToNetwork]);

    return <MapActionsContext value={value}>{children}</MapActionsContext>;
}
